import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { BarLoader } from 'react-spinners';
import EnrichedProductDetail from './EnrichedProductDetail';
import Field from './Field';
import BooleanField from './BooleanField';
import { Disclosure, Transition } from '@headlessui/react'
import { ChevronUpIcon } from '@heroicons/react/20/solid'


const StagedProductCard = ({ vendor, sharedAttributes, productType, products, onReEnrich }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formSharedAttributes, setFormSharedAttributes] = useState(sharedAttributes);
  const [formProducts, setFormProducts] = useState(products);
  const API_BASE = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL_BASE_DEV : process.env.REACT_APP_API_URL_BASE_PROD;
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    setFormSharedAttributes(sharedAttributes);
    setFormProducts(products);
  }, [sharedAttributes, products]);

  const ATTRIBUTE_ORDER = [
    'brands',
    'device_name',
    'line',
    'count',
    'volume',
    'nicotine_concentration_per_ml',
    'nicotine_concentration_percentage',
    'puff_count',
    'puff_range',
    'battery_capacity',
    'charger_type',
    'coil_type',
    'mouthpiece',
    'density_controls',
    'indicator_display'
  ];

  const BOOLEAN_ATTRIBUTE_ORDER = [
    'rechargeable',
    'charger_included',
    'e_liquid_indicator',
    'adjustable_airflow',
    'adjustable_wattage',
    'has_flavor_controls',
    'has_ice_control',
    'has_nicotine_control'
  ]

  const handleProductChange = (productIndex, attrId, newValue) => {
    setFormProducts(prev =>
      prev.map((product, idx) => {
        if (idx !== productIndex) return product;
        return {
          ...product,
          unique_product_attributes: product.unique_product_attributes.map(attr =>
            attr.id === attrId ? { ...attr, value: newValue } : attr
          )
        };
      })
    );
  };

  const handleProductCheckboxChange = (productIndex, attrId, key, newValue) => {
    setFormProducts(prev =>
      prev.map((product, idx) => {
        if (idx !== productIndex) return product;
        return {
          ...product,
          unique_product_attributes: product.unique_product_attributes.map(attr =>
            attr.id === attrId ? { ...attr, [key]: newValue } : attr
          )
        };
      })
    );
  };

  const handleVariantCheckboxChange = (productIndex, variantId, attrId, key, newValue) => {
    setFormProducts(prev =>
      prev.map((product, idx) => {
        if (idx !== productIndex) return product;
  
        const updatedVariants = { ...product.unique_variant_attributes };
        const updatedAttrs = updatedVariants[variantId].map(attr =>
          attr.id === attrId ? { ...attr, [key]: newValue } : attr
        );
  
        return {
          ...product,
          unique_variant_attributes: {
            ...updatedVariants,
            [variantId]: updatedAttrs
          }
        };
      })
    );
  };

  const handleVariantChange = (productIndex, variantId, attrId, newValue) => {
    setFormProducts(prev =>
      prev.map((product, idx) => {
        if (idx !== productIndex) return product;
  
        const updatedVariants = { ...product.unique_variant_attributes };
        const updatedAttrs = updatedVariants[variantId].map(attr =>
          attr.id === attrId ? { ...attr, value: newValue } : attr
        );
  
        return {
          ...product,
          unique_variant_attributes: {
            ...updatedVariants,
            [variantId]: updatedAttrs
          }
        };
      })
    );
  };

  const handleChange = (attributeId, key, value) => {
    setFormSharedAttributes(prevAttributes =>
      prevAttributes.map(attr =>
        attr.id === attributeId
          ? { ...attr, [key]: value }
          : attr
      )
    );
  };

  const handleCheckboxChange = (attributeId, key, value) => {
    setFormSharedAttributes(prev =>
      prev.map(attr =>
        attr.id === attributeId
          ? { ...attr, [key]: value }
          : attr
      )
    );
  };

  const handleSave = async () => {
    setIsLoading(true);
    
    const sharedPayloads = formSharedAttributes.filter(attr => attr.approved === true).map(attr => ({
        url: `${API_BASE}/api/shared_attributes/${attr.id}`,
        data: {
          shared_attribute: {
            value: attr.value,
            approved: attr.approved || false
          }
        }
      })
    );
  
    const productPayloads = formProducts.flatMap(product =>
      product.unique_product_attributes
        .filter(attr => attr.approved === true)
        .map(attr => ({
          url: `${API_BASE}/api/unique_product_attributes/${attr.id}`,
          data: {
            unique_product_attribute: {
              value: attr.value,
              approved: attr.approved || false
            }
          }
        })
      )
    );
  
    const variantPayloads = formProducts.flatMap(product =>
      Object.values(product.unique_variant_attributes).flatMap(variantGroup =>
        variantGroup
        .filter(attr => attr.approved === true)
        .map(attr => ({
          url: `${API_BASE}/api/unique_variant_attributes/${attr.id}`,
          data: {
            unique_variant_attribute: {
              value: attr.value,
              approved: attr.approved || false
            }
          }
        }))
      )
    );
  
    const allPayloads = [...sharedPayloads, ...productPayloads, ...variantPayloads]; 
  
    try {
      await Promise.all(
        allPayloads.map(({ url, data }) => axios.put(url, data))
      );
      await axios.post(`${API_BASE}/api/vendors/${vendor.id}/trigger_sync`);
      setIsLoading(false);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save attributes:', error);
    }
  };

  const handleReEnrich = async () => {
    setIsLoading(true);
    try{
      await onReEnrich(vendor.id);
      setIsLoading(false);
      setIsSuccess(true);
      setIsEditing(false);

      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to trigger Re-Enrich:', error);
      setIsLoading(false);
    }
  };

  const approveSharedSection = () => {
    setFormSharedAttributes(prev =>
      prev.map(attr =>
        ATTRIBUTE_ORDER.includes(attr.attribute_name) && attr.value && attr.value.toString().trim() !== ''
          ? { ...attr, approved: true }
          : attr
      )
    );
  };

  const approveBooleanSection = () => {
    setFormSharedAttributes(prev =>
      prev.map(attr =>
        BOOLEAN_ATTRIBUTE_ORDER.includes(attr.attribute_name) && attr.value && attr.value.toString().trim() !== ''
          ? { ...attr, approved: true }
          : attr
      )
    );
  };

  const approveProductAttributes = (productIndex) => {
    setFormProducts(prev =>
      prev.map((product, idx) => {
        if (idx !== productIndex) return product;
  
        const updatedAttrs = product.unique_product_attributes.map(attr =>
          attr.value && attr.value.toString().trim() !== ''
            ? { ...attr, approved: true }
            : attr
        );
  
        return { ...product, unique_product_attributes: updatedAttrs };
      })
    );
  };

  const approveVariantAttributes = (productIndex, variantId) => {
    setFormProducts(prev =>
      prev.map((product, idx) => {
        if (idx !== productIndex) return product;
  
        const updatedVariantGroup = {
          ...product.unique_variant_attributes,
          [variantId]: product.unique_variant_attributes[variantId].map(attr =>
            attr.value && attr.value.toString().trim() !== ''
              ? { ...attr, approved: true }
              : attr
          )
        };
  
        return { ...product, unique_variant_attributes: updatedVariantGroup };
      })
    );
  };

  const sortedLeftAttributes = formSharedAttributes
  .filter(attr => ATTRIBUTE_ORDER.includes(attr.attribute_name))
  .sort((a, b) => ATTRIBUTE_ORDER.indexOf(a.attribute_name) - ATTRIBUTE_ORDER.indexOf(b.attribute_name));

  const sortedRightAttributes = formSharedAttributes
  .filter(attr => BOOLEAN_ATTRIBUTE_ORDER.includes(attr.attribute_name))
  .sort((a, b) => BOOLEAN_ATTRIBUTE_ORDER.indexOf(a.attribute_name) - BOOLEAN_ATTRIBUTE_ORDER.indexOf(b.attribute_name));

  return (
    <div className="bg-white shadow-lg rounded-2xl w-full border border-gray-200 mx-auto relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div>
            <BarLoader color="#4F46E5" />
          </div>
        </div>
      )}
      {isSuccess && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-3xl flex gap-2 bg-emerald-500 p-8 rounded-lg text-white">
            <div className="animate-bounce">
              👍
            </div>
            <div className="font-bold">
              AI Refresh Successful!
            </div>
          </div>
        </div>
      )}
      <div className="bg-gray-50 border-b border-gray-200 rounded-t-2xl flex items-center justify-between p-6">
        <h2 className="text-xl font-bold">{vendor.vendor} - {productType}</h2>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="text-indigo-500 hover:text-indigo-600"
        >
          <FontAwesomeIcon icon={faPencil} />
        </button>
      </div>
      <div className="grid grid-cols-6 gap-6 p-6">
        <div className="col-span-4 pr-4 space-y-4">
          <h2 className="text-gray-500 mb-4">Product Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {sortedLeftAttributes.map(attr =>
                attr.value === 'true' || attr.value === 'false' ? (
                  <div key={attr.id}>
                    <BooleanField
                      attr={attr}
                      isEditing={isEditing}
                      onChange={(id, key, value) => handleChange(id, key, value)}
                      onCheckboxChange={(id, key, value) => handleCheckboxChange(id, key, value)}
                    />
                  </div>
                ) : (
                  <div key={attr.id}>
                    <Field 
                      attr={attr} 
                      isEditing={isEditing}
                      onChange={(id, key, value) => handleChange(id, key, value)}
                      onCheckboxChange={(id, key, value) => handleCheckboxChange(id, key, value)}
                    />
                  </div>
                )
            )}
          </div>
          {/* Full-width row for product_description */}
          {formSharedAttributes
            .filter(attr => attr.attribute_name === 'product_description')
            .map(attr => (
              <div key={attr.id} className="col-span-full">
                <Field 
                  attr={attr} 
                  isEditing={isEditing}
                  onChange={(id, key, value) => handleChange(id, key, value)}
                  onCheckboxChange={(id, key, value) => handleCheckboxChange(id, key, value)}
                />
              </div>
            ))}
            <button
              onClick={approveSharedSection}
              className="mt-2 text-sm text-indigo-600 hover:underline"
              hidden={!isEditing}
            >
              Approve Product Details Section
            </button>
        </div>

        {/* RIGHT SIDE */}
        {["Disposable", "Device", "Pods"].includes(productType) && (
        <div className="col-span-2 pl-4 border-l border-gray-100 space-y-4">
          <h2 className=" text-gray-500 mb-4">Boolean Attributes</h2>
          <div className="grid grid-cols-2 gap-4">
            {sortedRightAttributes.map(attr => (
              <div key={attr.id}>
                <BooleanField
                  attr={attr}
                  isEditing={isEditing}
                  onChange={(id, key, value) => handleChange(id, key, value)}
                  onCheckboxChange={(id, key, value) => handleCheckboxChange(id, key, value)}
                />
              </div>
              ))}
          </div>
          <button
            onClick={approveBooleanSection}
            className="mt-2 text-sm text-indigo-600 hover:underline"
            hidden={!isEditing}
          >
            Approve Boolean Attributes
          </button>
        </div>
        )}
        <div className="col-span-6 space-y-4">
        {["Disposable", "E-Liquid", "Nicotine Pouches"].includes(productType) && (
            <>
              <Disclosure as="div" className="border rounded-md bg-white shadow-sm">
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex justify-between w-full px-4 py-3 text-sm font-medium text-left text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75">
                            <span>Products</span>
                              <ChevronUpIcon
                                className={`${
                                  open ? 'rotate-180 transform' : ''
                                } h-5 w-5 text-indigo-500`}
                              />
                        </Disclosure.Button>
                        <Transition
                          show={open}
                          enter="transition-all duration-300 ease-out"
                          enterFrom="max-h-0 opacity-0"
                          enterTo="max-h-[1000px] opacity-100"
                          leave="transition-all duration-200 ease-in"
                          leaveFrom="max-h-[1000px] opacity-100"
                          leaveTo="max-h-0 opacity-0"
                        >
                          <Disclosure.Panel>
                            <div className="p-6 justify-between">
                              {formProducts.map((product, idx) => (
                                <EnrichedProductDetail
                                  key={idx}
                                  title={product.title}
                                  uniqueProductAttributes={product.unique_product_attributes}
                                  uniqueVariantAttributes={product.unique_variant_attributes}
                                  isEditing={isEditing}
                                  Field={Field}
                                  productIndex={idx}
                                  BooleanField={BooleanField}
                                  onChange={handleProductChange}
                                  onCheckboxChange={handleProductCheckboxChange}
                                  onVariantChange={handleVariantChange}
                                  onVariantCheckboxChange={handleVariantCheckboxChange}
                                  approveProductAttributes={approveProductAttributes}
                                  approveVariantAttributes={approveVariantAttributes}
                                />
                              ))}
                            </div>
                          </Disclosure.Panel>
                        </Transition>
                      </>
                    )}
                    </Disclosure>
            </>
          )}
        </div>
      </div>
      {isEditing && (
        <div>
          <button
            onClick={handleSave}
            className="my-4 mx-6 bg-indigo-600 text-white px-4 py-2 rounded-md"
          >
            Save All
          </button>
          <button
            onClick={handleReEnrich}
            className="my-4 mx-6 bg-indigo-600 text-white px-4 py-2 rounded-md"
          >
            { isLoading ? <BarLoader/> : "Re-Trigger AI" }
          </button>
        </div>
      )}
    </div>    
  );
};

export default StagedProductCard;